import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { apiFetch } from '../../lib/api-client'
import { t } from '../../lib/i18n'

interface Renter {
  id: string
  name: string
  email: string
  phone: string | null
  status: string
  has_cpr: boolean
  party_kind: string
}

function statusVariant(s: string): 'green' | 'amber' | 'gray' {
  switch (s) {
    case 'active': return 'green'
    case 'applicant': return 'amber'
    default: return 'gray'
  }
}

export function RenterListPage() {
  const strings = t()
  const navigate = useNavigate()
  const [renters, setRenters] = React.useState<Renter[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const generalError = strings.general.error

  React.useEffect(() => {
    apiFetch<Renter[]>('/api/v1/renters')
      .then(setRenters)
      .catch(() => setError(generalError))
      .finally(() => setLoading(false))
  }, [generalError])

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      applicant: strings.renters.statusApplicant,
      active: strings.renters.statusActive,
      former: strings.renters.statusFormer,
    }
    return map[s] ?? s
  }

  return (
    <AppLayout
      actions={
        <Link to="/lejere/opret">
          <Button size="sm">
            <Plus size={13} style={{ marginRight: 4 }} />
            {strings.renters.create}
          </Button>
        </Link>
      }
    >
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px 12px',
          borderBottom: '1px solid #e8eaed',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
            {strings.renters.title} {!loading && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 13 }}>({renters.length})</span>}
          </span>
        </div>

        {loading && <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>}
        {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}

        {!loading && !error && renters.length === 0 && (
          <div style={{ padding: 56, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>{strings.renters.noResults}</p>
            <Link to="/lejere/opret"><Button size="sm"><Plus size={13} style={{ marginRight: 4 }} />{strings.renters.create}</Button></Link>
          </div>
        )}

        {!loading && !error && renters.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[strings.renters.name, strings.renters.email, strings.renters.phone, strings.renters.cpr, strings.renters.status, ''].map((h) => (
                  <th key={h} style={{ padding: '10px 24px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renters.map((r, i) => (
                <RenterRow key={r.id} renter={r} statusLabel={statusLabel(r.status)} isLast={i === renters.length - 1} strings={strings} onClick={() => navigate(`/lejere/${r.id}`)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  )
}

function RenterRow({ renter: r, statusLabel, isLast, strings, onClick }: {
  renter: Renter
  statusLabel: string
  isLast: boolean
  strings: ReturnType<typeof t>
  onClick: () => void
}) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? '#f9fafb' : 'transparent', transition: 'background 0.1s', borderBottom: isLast ? 'none' : '1px solid #f3f4f6', cursor: 'pointer' }}
    >
      <td style={{ padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={r.name} size={28} />
          <span style={{ fontWeight: 500, color: '#111827' }}>{r.name}</span>
          {r.party_kind === 'selskab' && (
            <Chip variant="blue">{strings.renters.selskabBadge}</Chip>
          )}
        </div>
      </td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>{r.email}</td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>{r.phone ?? <span style={{ color: '#d1d5db' }}>—</span>}</td>
      <td style={{ padding: '12px 24px' }}>
        <Chip variant={r.has_cpr ? 'blue' : 'gray'}>{r.has_cpr ? strings.renters.hasCpr : strings.renters.noCpr}</Chip>
      </td>
      <td style={{ padding: '12px 24px' }}>
        <Chip variant={statusVariant(r.status)}>{statusLabel}</Chip>
      </td>
      <td style={{ padding: '12px 24px', textAlign: 'right', color: '#9ca3af', fontSize: 13 }}>→</td>
    </tr>
  )
}
